import { track } from "../../clients/mixpanel";
import { Unit, UnitSpec, UnitSpecAssociation } from "../../interfaces/Unit";
import { ReportUseGroup } from "../../interfaces/EstimateUseGroup";
import { getEstimateReportData, setPrintAreaToUsedRange } from "../workbook";
import { ValidationError } from "../../errors";
import { ColumnNameToIndexMap } from "./helpers/common";
import { CellOverride, RowFormat, createColumnNameToIndexMap, printFormattedData } from "./helpers/print";
import { handleDynamicReportGeneration, SheetDataItem } from "./helpers/dynamicSheets";
import { handleDynamicColumns } from "./helpers/print";
import { cacheableData } from "../workbook/cache";
import { columnNames, rowNames } from "./helpers/rangeNames";
import { fetchUnitSpecs, fetchUnitSpecAssociations } from "../../api/units";
import { FloorAreaItem } from "../../interfaces/AreaSheet";
import { fetchUnitSort } from "../../api/settings";
import { fetchEstimateExport } from "../../api/estimates";
import { transformFloorAreas } from "../datasources/floorAreas";
import { transformUseGroups } from "../datasources/useGroups";
import { getEstimateUnits } from "../datasources/units";
import { UNIT_MIX_REPORT_NAME } from "./api/reportNames";

export interface UnitMixReportOptions extends cacheableData {
  // Add any specific options if needed
}

interface FloorUnitData {
  floorId: string;
  floorName: string;
  unitCounts: Record<string, number>; // unitTypeId -> count
  netArea: number;
  grossArea: number;
  totalUnits: number;
}

interface UnitTypeData {
  id: string;
  label: string;
  area: number;
  created_at: string;
  specs: Record<string, number>; // spec key -> value (beds, baths, half_baths, area)
  qtoData?: Array<{ value: number; total_uc: number }>; // quantity take off data from line items
  qtoCost?: number; // total cost from QTO line items
}

export const unitMixDefaultOptions = {} as UnitMixReportOptions;

export const fetchUnitsWithUseGroupName = async (estimateIdParam?: string): Promise<Unit[]> => {
  let estimateId = estimateIdParam;

  if (!estimateId) {
    const reportData = (await getEstimateReportData()) as any[];
    estimateId = reportData[0]?.estimate_id as string | undefined;
  }
  if (!estimateId) return [];

  const response = await fetchEstimateExport(estimateId);
  if (!response.success) throw response.error;
  return response.data ? getEstimateUnits(response.data) : [];
};

export const generateUnitMixReports = async (context: Excel.RequestContext, templateWorksheet: Excel.Worksheet) => {
  const startTime = performance.now();

  // Get estimate ID from the Report table
  const reportData = (await getEstimateReportData()) as any[];
  if (reportData.length === 0) {
    throw new ValidationError("No estimate loaded. Please load an estimate first.");
  }

  const estimateId = reportData[0].estimate_id;
  if (!estimateId) {
    throw new ValidationError("Estimate ID is required to generate Unit Mix report.");
  }

  // Fetch all required data
  const [specsResult, associationsResult, unitSortResult, estimateResult] = await Promise.all([
    fetchUnitSpecs(),
    fetchUnitSpecAssociations(),
    fetchUnitSort(estimateId),
    fetchEstimateExport(estimateId),
  ]);

  if (
    !estimateResult.success ||
    !specsResult.success ||
    !associationsResult.success ||
    !estimateResult.data ||
    !specsResult.data ||
    !associationsResult.data
  ) {
    throw new ValidationError("Failed to fetch required data for Unit Mix report.");
  }

  const units = getEstimateUnits(estimateResult.data);
  const unitSpecs = specsResult.data;
  const unitSpecAssociations = associationsResult.data;
  const sortOrder =
    unitSortResult.success && unitSortResult.data?.data?.value ? unitSortResult.data.data.value : "a-to-z";

  if (units.length === 0) {
    throw new ValidationError("Load an estimate with units before generating a Unit Mix report.");
  }

  const estimateData = estimateResult.success && estimateResult.data ? estimateResult.data : null;
  const floorAreas = estimateData
    ? transformFloorAreas(
        estimateData.area_matrix_items,
        estimateData.area_matrix_item_estimate_use_groups,
        estimateData.estimate_use_groups,
        estimateData.floor_groups
      )
    : [];
  // Only include line items from accepted VM decisions (or base-bid items with no alternate).
  // The in-app path uses useAcceptedLineItemRows to achieve this (MDUCK-2134).
  const acceptedAlternateIds = new Set(
    (estimateData?.alternates ?? []).filter((alt) => alt.accepted).map((alt) => alt.id)
  );
  const estimateLineItems = (estimateData?.line_items ?? []).filter(
    (li) => !li.alternate || acceptedAlternateIds.has(li.alternate)
  );
  const quantityTakeOffs = estimateData?.quantity_take_offs ?? [];

  // Get use groups to generate sheets for each one
  const useGroups = estimateData
    ? transformUseGroups(estimateData.estimate.stats.useGroups, estimateData.floor_groups)
    : [];
  useGroups.sort((a, b) => a.order - b.order);

  const useGroupDictionary = useGroups.reduce(
    (acc, useGroup) => {
      acc[useGroup.id] = useGroup.name;
      return acc;
    },
    {} as Record<string, string>
  );

  const mappedUnits = units.map((unit) => {
    return {
      ...unit,
      estimate_use_group_name: useGroupDictionary[unit.estimate_use_group],
    };
  });

  const sheets = await handleDynamicReportGeneration(
    context,
    templateWorksheet,
    UNIT_MIX_REPORT_NAME,
    mappedUnits,
    "estimate_use_group",
    "estimate_use_group_name"
  );

  // For each sheet, call generateUnitMixReport
  for (let i = 0; i < sheets.length; i++) {
    const sheetItem = sheets[i];
    await generateUnitMixReport(
      sheetItem,
      useGroups,
      unitSpecs,
      unitSpecAssociations,
      sortOrder,
      floorAreas,
      estimateLineItems,
      quantityTakeOffs
    );
  }

  track("excel.createReport.unitMix", {
    loadTime: (performance.now() - startTime) / 1000,
  });
};

export const generateUnitMixReport = async (
  sheetData: SheetDataItem,
  useGroups: ReportUseGroup[],
  unitSpecs: UnitSpec[],
  unitSpecAssociations: UnitSpecAssociation[],
  sortOrder: string,
  floorAreas: FloorAreaItem[],
  allLineItems: any[],
  quantityTakeOffs: any[]
) => {
  await Excel.run(async (context) => {
    const worksheet = context.workbook.worksheets.getItem(sheetData.sheetName);
    const units = sheetData.data as Unit[];

    const useGroup = useGroups.find((ug) => ug.id === sheetData.key);
    if (!useGroup) {
      throw new ValidationError(`Use group ${sheetData.key} not found.`);
    }

    const floorAreasMap = new Map<string, FloorAreaItem>();
    floorAreas.forEach((floor) => {
      floorAreasMap.set(floor.id, floor);
    });

    // Build a map of line items by id for O(1) lookup.
    // The FK lives on quantity_take_offs.line_item → line_items.id, not the reverse.
    const lineItemsById = new Map<string, any>(allLineItems.map((li) => [li.id, li]));

    // Create a mapping of unit_type.id -> array of {value, total_uc} by iterating QTOs
    const unitQTOMap = new Map<string, Array<{ value: number; total_uc: number }>>();

    quantityTakeOffs.forEach((qto: any) => {
      const lineItem = qto.line_item ? lineItemsById.get(qto.line_item) : undefined;
      if (!lineItem || !qto.extras?.quantity_take_off_units) return;

      // Deduplicate unit entries within a single QTO: the in-app path stores
      // units as object properties keyed by unit ID, so duplicates collapse
      // naturally.  Mirror that here by keeping only the last entry per unit.
      const dedupedUnits = new Map<string, any>();
      qto.extras.quantity_take_off_units.forEach((unitData: any) => {
        dedupedUnits.set(unitData.id, unitData);
      });

      dedupedUnits.forEach((unitData, unitId) => {
        if (!unitQTOMap.has(unitId)) {
          unitQTOMap.set(unitId, []);
        }
        unitQTOMap.get(unitId)!.push({
          value: unitData.value || 0,
          total_uc: lineItem.total_uc || 0,
        });
      });
    });

    // Filter unit spec associations for this use group
    const useGroupAssociations = unitSpecAssociations.filter((assoc) => assoc.use_group === useGroup.use_group_id);

    // Get unique unit types from the units and collect spec info
    const unitTypes: UnitTypeData[] = [];
    const unitTypeMap = new Map<string, UnitTypeData>();
    const specInfoMap = new Map<string, UnitSpec>();

    units.forEach((unit) => {
      if (!unitTypeMap.has(unit.id)) {
        const unitType: UnitTypeData = {
          id: unit.id,
          label: unit.label,
          area: unit.area,
          created_at: unit.created_at,
          specs: { area: unit.area },
        };

        // Get specs for this unit type
        unit.unit_type_unit_spec_associations?.forEach((assoc) => {
          const specAssoc = useGroupAssociations.find((a) => a.id === assoc.unit_spec_association);
          if (specAssoc) {
            const spec = unitSpecs.find((s) => s.id === specAssoc.unit_spec);
            if (spec) {
              unitType.specs[spec.key] = assoc.value;
              // Collect spec info for later use in the report
              if (!specInfoMap.has(spec.key)) {
                specInfoMap.set(spec.key, spec);
              }
            }
          }
        });

        // Add area to specInfoMap if not already present
        if (!specInfoMap.has("area")) {
          specInfoMap.set("area", {
            id: "area",
            key: "area",
            name: "Area",
            type: "number",
          });
        }

        // Attach QTO data if available
        const qtoData = unitQTOMap.get(unit.id);
        if (qtoData) {
          unitType.qtoData = qtoData;
          // Calculate total QTO cost
          unitType.qtoCost = qtoData.reduce((sum, item) => sum + item.value * item.total_uc, 0);
        }

        unitTypes.push(unitType);
        unitTypeMap.set(unit.id, unitType);
      }
    });

    // Convert specInfoMap to array for passing to unitMixArray
    const specInfoArray = Array.from(specInfoMap.values());

    // Process floor data from extras.floor_unit_types
    const floorDataMap = new Map<string, FloorUnitData>();

    units.forEach((unit) => {
      if (unit.extras?.floor_unit_types) {
        unit.extras.floor_unit_types.forEach((floorUnit) => {
          // Skip floors where value is null or undefined (This is a catch for DI discrepancies)
          if (floorUnit.value == null) {
            return;
          }

          if (!floorDataMap.has(floorUnit.id)) {
            const floorAreaItem = floorAreasMap.get(floorUnit.id);
            floorDataMap.set(floorUnit.id, {
              floorId: floorUnit.id,
              floorName: floorAreaItem?.label || `Floor ${floorUnit.id}`,
              unitCounts: {},
              netArea: 0,
              grossArea:
                floorAreaItem?.[`${useGroup.name}_area`] ?? floorAreaItem?.[`${useGroup.name}_exclude_area`] ?? 0,
              totalUnits: 0,
            });
          }

          const floorData = floorDataMap.get(floorUnit.id)!;
          floorData.unitCounts[unit.id] = (floorData.unitCounts[unit.id] || 0) + floorUnit.value;
          floorData.totalUnits += floorUnit.value;

          // Calculate areas
          const unitArea = unitTypeMap.get(unit.id)?.area || 0;
          floorData.netArea += unitArea * floorUnit.value;
        });
      }
    });

    // Order floors to match the Area Sheet report. transformFloorAreas already emits floors in
    // display order (above-grade descending, then below-grade ascending, then features, grouped by
    // floor group), so we reuse that ordering rather than re-deriving it. Floor numbers restart at
    // 1 within each floor type, so sorting by floor_number alone would interleave below-grade floors
    // with above-grade ones.
    const areaSheetOrder = new Map<string, number>(floorAreas.map((floor, index) => [floor.id, index]));
    const floors = Array.from(floorDataMap.values()).sort(
      (a, b) =>
        (areaSheetOrder.get(a.floorId) ?? Number.MAX_SAFE_INTEGER) -
        (areaSheetOrder.get(b.floorId) ?? Number.MAX_SAFE_INTEGER)
    );

    // Create dynamic columns for unit types
    // Sort based on user preference and transform to have 'name' property for handleDynamicColumns
    const sortedUnitTypes = unitTypes.sort((a, b) => {
      switch (sortOrder) {
        case "a-to-z":
          return a.label.localeCompare(b.label);
        case "z-to-a":
          return b.label.localeCompare(a.label);
        case "newest":
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case "oldest":
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        default:
          return a.label.localeCompare(b.label);
      }
    });

    const unitTypesWithName = sortedUnitTypes.map((ut) => ({ ...ut, name: ut.label }));
    await handleDynamicColumns(worksheet, columnNames.unitTypes, unitTypesWithName, {
      wrapText: true,
      copyFormat: true,
    });

    const columnMap = await createColumnNameToIndexMap(worksheet);

    // Get the starting column index for unit types
    const unitTypesStartColumn = columnMap[columnNames.unitTypes];

    const { mappedArray, formatList } = unitMixArray(
      floors,
      unitTypes,
      specInfoArray,
      columnMap,
      unitTypesStartColumn,
      useGroup
    );

    await printFormattedData({
      dataArray: mappedArray,
      formatList,
      worksheet,
      insertAtRangeName: rowNames.unitMixItems,
      startColumnIndex: 1,
      options: {},
    });

    await setPrintAreaToUsedRange(worksheet);
  });
};

const unitMixArray = (
  floors: FloorUnitData[],
  unitTypes: UnitTypeData[],
  specInfoArray: UnitSpec[],
  columnMap: ColumnNameToIndexMap,
  unitTypesStartColumn: number | undefined,
  useGroup: ReportUseGroup
) => {
  // Calculate the total number of columns
  const maxColumnIndex = Math.max(...Object.values(columnMap).filter((v): v is number => typeof v === "number"));
  const totalColumns = maxColumnIndex + 1;
  const unitsCellOverrides = [
    { columnIndex: 2, style: "spacerOuterBorders" }, // 1st spacer
    { columnIndex: totalColumns - 4, style: "spacerOuterBorders", width: 12 }, // 2nd spacer
    { columnIndex: totalColumns, style: "spacerOuterBorderLeft", width: 20 }, // end
  ] as CellOverride[];

  const specsHeaderCellOverrides = [
    { columnIndex: 2, style: "spacerOuterBorders" }, // 1st spacer
    { columnIndex: totalColumns - 4, style: "spacerOuterBorderLeft", width: 12 }, // 2nd spacer
    { columnIndex: totalColumns - 3, style: "spacerOuterBorderTop" }, // net area
    { columnIndex: totalColumns - 2, style: "spacerOuterBorderTop" }, // gross area
    { columnIndex: totalColumns - 1, style: "spacerOuterBorderTop" }, // efficiency
    { columnIndex: totalColumns, style: "item" }, // end
  ] as CellOverride[];

  const specsAndTotalsCellOverrides = [
    { columnIndex: 2, style: "spacerOuterBorders" }, // 1st spacer
    { columnIndex: totalColumns - 4, style: "spacerOuterBorderLeft", width: 12 }, // 2nd spacer
    { columnIndex: totalColumns - 3, style: "item" }, // net area
    { columnIndex: totalColumns - 2, style: "item" }, // gross area
    { columnIndex: totalColumns - 1, style: "item" }, // efficiency
    { columnIndex: totalColumns, style: "item" }, // end
  ] as CellOverride[];

  const formatList: RowFormat[] = [];
  const mappedArray: (string | number)[][] = [];

  // Floor rows
  floors.forEach((floor) => {
    const row: (string | number)[] = new Array(totalColumns).fill("");

    // Floor name
    if (columnMap["floor_name"] !== undefined) {
      row[columnMap["floor_name"]] = floor.floorName;
    }

    // Total units
    if (columnMap["totals"] !== undefined) {
      row[columnMap["totals"]] = floor.totalUnits;
    }

    // Unit counts per type
    if (unitTypesStartColumn !== undefined) {
      unitTypes.forEach((unitType, index) => {
        row[unitTypesStartColumn + index] = floor.unitCounts[unitType.id] || 0;
      });
    }

    // Net and gross area
    if (columnMap["net_area"] !== undefined) {
      row[columnMap["net_area"]] = floor.netArea;
    }
    if (columnMap["gross_area"] !== undefined) {
      row[columnMap["gross_area"]] = floor.grossArea;
    }
    if (columnMap["efficiency"] !== undefined && floor.grossArea > 0) {
      row[columnMap["efficiency"]] = floor.netArea / floor.grossArea;
    }

    mappedArray.push(row);
    formatList.push({
      style: "gridItem",
      level: 0,
      indentColumn: undefined,
      group: [],
      cellOverrides: unitsCellOverrides,
    });
  });

  // Unit Specs header
  const specsHeaderRow: (string | number)[] = new Array(totalColumns).fill("");
  specsHeaderRow[0] = "Unit Specs";
  mappedArray.push(specsHeaderRow);
  formatList.push({
    style: "unitSpecsHeader",
    level: 0,
    indentColumn: undefined,
    group: [],
    cellOverrides: specsHeaderCellOverrides,
  });

  // Specs rows (dynamically generated from actual unit specs)
  specInfoArray.forEach((specInfo) => {
    const row: (string | number)[] = new Array(totalColumns).fill("");
    row[0] = specInfo.name;

    let specTotal = 0;
    if (unitTypesStartColumn !== undefined) {
      unitTypes.forEach((unitType, index) => {
        const specValue = unitType.specs[specInfo.key] || 0;
        row[unitTypesStartColumn + index] = specValue;

        // Calculate weighted total (spec value * unit count)
        const unitCount = floors.reduce((sum, floor) => sum + (floor.unitCounts[unitType.id] || 0), 0);
        specTotal += specValue * unitCount;
      });
    }

    // Add total to the totals column
    if (columnMap["totals"] !== undefined) {
      row[columnMap["totals"]] = specTotal;
    }

    mappedArray.push(row);
    formatList.push({
      style: "gridItem",
      level: 0,
      indentColumn: undefined,
      group: [],
      cellOverrides: specsAndTotalsCellOverrides,
    });
  });

  // Totals header
  const totalsHeaderRow: (string | number)[] = new Array(totalColumns).fill("");
  totalsHeaderRow[0] = "Totals";
  mappedArray.push(totalsHeaderRow);
  formatList.push({
    style: "unitTotalsHeader",
    level: 0,
    indentColumn: undefined,
    group: [],
    cellOverrides: specsAndTotalsCellOverrides,
  });

  // Helper function to add a totals row
  const addTotalRow = (label: string, valueSource: ((unitType: UnitTypeData) => number) | string) => {
    const row: (string | number)[] = new Array(totalColumns).fill("");
    row[0] = label;
    const useFormula = typeof valueSource === "string";

    let total = 0;
    if (unitTypesStartColumn !== undefined) {
      unitTypes.forEach((unitType, index) => {
        const value = useFormula ? valueSource : valueSource(unitType);
        row[unitTypesStartColumn + index] = value;
        if (typeof value === "number") {
          total += value;
        }
      });
    }

    if (columnMap["totals"] !== undefined) {
      row[columnMap["totals"]] = useFormula ? valueSource : total;
    }

    mappedArray.push(row);
    formatList.push({
      style: "gridItem",
      level: 0,
      indentColumn: undefined,
      group: [],
      cellOverrides: specsAndTotalsCellOverrides,
    });
  };

  // Total Cost (qtoCost * unitCount)
  addTotalRow("Total Cost", (unitType) => {
    const qtoCost = unitType.qtoCost || 0;
    const unitCount = floors.reduce((sum, floor) => sum + (floor.unitCounts[unitType.id] || 0), 0);
    return qtoCost * unitCount;
  });

  // Cost per Unit
  addTotalRow(useGroup.unit_cost_label ? `Cost/${useGroup.unit_cost_label}` : "Cost/Unit", "=IFERROR(R[-1]C/R[1]C, 0)");

  // Unit Count
  addTotalRow(useGroup.unit_cost_label ? `${useGroup.unit_cost_label} Count` : "Unit Count", (unitType) =>
    floors.reduce((sum, floor) => sum + (floor.unitCounts[unitType.id] || 0), 0)
  );

  // Total Unit Area
  addTotalRow("Net Area", (unitType) => {
    const unitArea = unitType.specs["area"] || 0;
    const unitCount = floors.reduce((sum, floor) => sum + (floor.unitCounts[unitType.id] || 0), 0);
    return unitArea * unitCount;
  });

  return { mappedArray, formatList };
};
